import React, { useCallback, useEffect, useRef, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { DraggableProvided, DraggableStateSnapshot, DropResult } from '@hello-pangea/dnd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import * as yup from 'yup';
import notify from 'lib/notify';
import { getTablePreferences, setTablePreferences } from 'components/utils';

const isValidHttpUrl = (value: string) => {
  try {
    const url = new URL(/^(https?:)?\/\//i.test(value) ? value : `https://${value}`);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

export const urlSchema = yup
  .string()
  .nullable()
  .transform(value => (value === '' ? null : value))
  .test('url-format', 'Invalid URL format', value => value == null || isValidHttpUrl(value));

export const READ_MODE_TEXT_MAX_WIDTH = '120px';

export const readModeTextClampSx = (maxWidth: string = READ_MODE_TEXT_MAX_WIDTH) => ({
  display: 'inline-block',
  maxWidth: maxWidth,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'bottom',
});

export const extractTextFromNode = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node
      .map(item => extractTextFromNode(item))
      .join('')
      .trim();
  }

  if (React.isValidElement(node)) {
    const elementChildren = (node.props as { children?: React.ReactNode }).children;
    return extractTextFromNode(elementChildren);
  }

  return '';
};

export const ReadModeText = ({ text, maxWidth = READ_MODE_TEXT_MAX_WIDTH }: { text: string; maxWidth?: string }) => {
  return (
    <ReadModeValueWrapper text={text} maxWidth={maxWidth}>
      {text}
    </ReadModeValueWrapper>
  );
};

export const ReadModeValueWrapper = ({
  text,
  maxWidth = READ_MODE_TEXT_MAX_WIDTH,
  children,
}: {
  text: string;
  maxWidth?: string;
  children: React.ReactNode;
}) => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = textRef.current;

    if (!element) {
      setIsOverflowing(false);
      return;
    }

    const hasScrollOverflow = element.scrollWidth > element.clientWidth + 1;

    if (hasScrollOverflow) {
      setIsOverflowing(true);
      return;
    }

    if (typeof window === 'undefined' || !text || element.clientWidth <= 0) {
      setIsOverflowing(false);
      return;
    }

    const measureEl = window.document.createElement('span');
    const computed = window.getComputedStyle(element);
    measureEl.textContent = text;
    measureEl.style.position = 'absolute';
    measureEl.style.visibility = 'hidden';
    measureEl.style.pointerEvents = 'none';
    measureEl.style.whiteSpace = 'nowrap';
    measureEl.style.font = computed.font;
    measureEl.style.fontFamily = computed.fontFamily;
    measureEl.style.fontSize = computed.fontSize;
    measureEl.style.fontWeight = computed.fontWeight;
    measureEl.style.fontStyle = computed.fontStyle;
    measureEl.style.letterSpacing = computed.letterSpacing;
    measureEl.style.textTransform = computed.textTransform;

    window.document.body.appendChild(measureEl);
    const measuredWidth = measureEl.getBoundingClientRect().width;
    window.document.body.removeChild(measureEl);

    setIsOverflowing(measuredWidth > element.clientWidth + 1);
  }, [text]);

  useEffect(() => {
    checkOverflow();
  }, [checkOverflow, text, maxWidth]);

  useEffect(() => {
    const element = textRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [checkOverflow, text, maxWidth]);

  return (
    <Tooltip
      title={isOverflowing ? text : ''}
      arrow
      disableInteractive
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
    >
      <Box ref={textRef} component="span" sx={readModeTextClampSx(maxWidth)}>
        {children}
      </Box>
    </Tooltip>
  );
};

export const renderReadModeValue = (node: React.ReactNode, maxWidth: string = READ_MODE_TEXT_MAX_WIDTH) => {
  const text = extractTextFromNode(node);

  if (!text) {
    return node;
  }

  if (React.isValidElement(node)) {
    return (
      <ReadModeValueWrapper text={text} maxWidth={maxWidth}>
        {node}
      </ReadModeValueWrapper>
    );
  }

  return <ReadModeText text={text} maxWidth={maxWidth} />;
};

export const CopyValueButton = ({
  value,
  tooltipTitle,
  successMessage,
}: {
  value: string;
  tooltipTitle: string;
  successMessage: string;
}) => {
  return (
    <Tooltip title={tooltipTitle}>
      <span style={{ display: 'inline-flex' }}>
        <CopyToClipboard text={value} onCopy={() => notify(successMessage)}>
          <IconButton size="small" sx={{ p: 0.25 }}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </CopyToClipboard>
      </span>
    </Tooltip>
  );
};

/** Shared with ColumnsManager, CommonStatusList “Change order” — bottom-right anchored popover shell */
export const reorderPopoverAnchorOrigin = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};

export const reorderPopoverTransformOrigin = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

export const reorderPopoverSlotProps = {
  paper: {
    sx: (theme: Theme) => ({
      bgcolor: 'background.paper',
      color: 'text.primary',
      border: `1px solid ${theme.palette.divider}`,
      mt: 1,
    }),
  },
};

export const reorderPopoverScrollContentSx = {
  minWidth: 320,
  maxHeight: 620,
  overflowY: 'auto' as const,
  p: 0.5,
  display: 'flex',
  flexDirection: 'column' as const,
};

type ReorderPopoverSlotProps = {
  root?: { sx?: Record<string, unknown> };
  paper?: { sx?: Record<string, unknown> | ((theme: Theme) => Record<string, unknown>) };
};

export function ReorderPopover({
  open,
  anchorEl,
  onClose,
  children,
  popoverSlotProps,
  popoverTestId,
  hideBackdrop = false,
}: {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
  popoverSlotProps?: ReorderPopoverSlotProps;
  popoverTestId?: string;
  hideBackdrop?: boolean;
}) {
  const defaultPaperSx = reorderPopoverSlotProps.paper.sx;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      anchorOrigin={reorderPopoverAnchorOrigin}
      transformOrigin={reorderPopoverTransformOrigin}
      slotProps={{
        ...popoverSlotProps,
        paper: {
          ...reorderPopoverSlotProps.paper,
          ...popoverSlotProps?.paper,
          ...(popoverTestId ? { 'data-testid': popoverTestId } : {}),
          sx: (theme: Theme) => ({
            ...(typeof defaultPaperSx === 'function' ? defaultPaperSx(theme) : defaultPaperSx),
            ...(typeof popoverSlotProps?.paper?.sx === 'function'
              ? popoverSlotProps.paper.sx(theme)
              : popoverSlotProps?.paper?.sx),
          }),
        },
        root: {
          ...popoverSlotProps?.root,
        },
      }}
    >
      <Box sx={reorderPopoverScrollContentSx}>{children}</Box>
    </Popover>
  );
}

const dragReorderHandleBoxSx = {
  flexShrink: 0,
  color: 'text.secondary',
  cursor: 'grab',
  lineHeight: 0,
  display: 'flex',
  alignItems: 'center',
};

/** One row inside @hello-pangea/dnd Draggable — handle + label + optional trailing (e.g. checkbox) */
export function DragReorderRow({
  draggableProvided,
  snapshot,
  label,
  trailing,
}: {
  draggableProvided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  label: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <Box
      ref={draggableProvided.innerRef}
      {...draggableProvided.draggableProps}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        color: 'text.primary',
        bgcolor: snapshot.isDragging ? 'action.selected' : 'transparent',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <Box {...draggableProvided.dragHandleProps} sx={dragReorderHandleBoxSx}>
        <DragIndicatorIcon fontSize="small" />
      </Box>

      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {label}
      </Typography>

      {trailing}
    </Box>
  );
}

export function toggleReorderPopoverAnchor(
  setter: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>,
  event: React.MouseEvent<HTMLButtonElement>,
): void {
  setter(current => (current ? null : event.currentTarget));
}

export function isTrivialDragResult(result: DropResult): boolean {
  if (!result.destination) {
    return true;
  }

  if (result.source.index === result.destination.index) {
    return true;
  }

  return false;
}

export function reorderArrayByMove<T>(list: T[], sourceIndex: number, destinationIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(sourceIndex, 1);
  result.splice(destinationIndex, 0, removed);

  return result;
}

export type FilterStatusItem = {
  _id: string;
  label?: string;
  name?: string;
};

export type SavedStatusFilterVisibility = {
  name: string;
  isVisible: boolean;
};

export type StatusWithVisibility = FilterStatusItem & {
  isVisible: boolean;
};

export type StatusFilterSelectionFormat = 'all-ids' | 'empty-when-all';

export const STATUS_FILTER_VISIBILITY_CONFIGURED_KEY = 'statusFilterVisibilityConfigured';

export const getSavedStatusFilterVisibility = (tableName: string): SavedStatusFilterVisibility[] | undefined => {
  if (getTablePreferences(tableName)[STATUS_FILTER_VISIBILITY_CONFIGURED_KEY] !== true) {
    return undefined;
  }

  return (getTablePreferences(tableName).statusFilterVisibility || []) as SavedStatusFilterVisibility[];
};

export const toSavedStatusFilterVisibility = (statuses: StatusWithVisibility[]): SavedStatusFilterVisibility[] =>
  statuses.map(status => ({
    name: status._id,
    isVisible: status.isVisible,
  }));

export const applySavedStatusFilterVisibility = <T extends FilterStatusItem>({
  statuses,
  savedVisibility,
}: {
  statuses: T[];
  savedVisibility?: SavedStatusFilterVisibility[];
}): (T & { isVisible: boolean })[] => {
  if (savedVisibility === undefined) {
    return statuses.map(status => ({ ...status, isVisible: true }));
  }

  const visibilityByName = new Map(savedVisibility.map(item => [item.name, item.isVisible]));

  return statuses.map(status => ({
    ...status,
    isVisible: visibilityByName.has(status._id) ? Boolean(visibilityByName.get(status._id)) : true,
  }));
};

export const getVisibleStatuses = <T extends StatusWithVisibility>(statuses: T[]): T[] =>
  statuses.filter(status => status.isVisible);

export const getAllVisibleStatusFilterValue = (
  statuses: StatusWithVisibility[],
  format: StatusFilterSelectionFormat = 'all-ids',
): string => {
  const visibleIds = getVisibleStatuses(statuses).map(status => status._id);

  if (format === 'empty-when-all') {
    return '';
  }

  return visibleIds.join(',');
};

export const persistStatusFilterSettings = ({
  tableName,
  statuses,
  format = 'all-ids',
}: {
  tableName: string;
  statuses: StatusWithVisibility[];
  format?: StatusFilterSelectionFormat;
}): string => {
  const statusFilter = getAllVisibleStatusFilterValue(statuses, format);

  setTablePreferences({
    tableName,
    values: [
      {
        key: 'statusFilterVisibility',
        value: toSavedStatusFilterVisibility(statuses),
      },
      {
        key: STATUS_FILTER_VISIBILITY_CONFIGURED_KEY,
        value: true,
      },
      {
        key: 'statusFilter',
        value: statusFilter,
      },
    ],
  });

  return statusFilter;
};

export const getManageableStatusesDisplayOrder = <T extends StatusWithVisibility>(statuses: T[]): T[] => [
  ...statuses.filter(status => status.isVisible),
  ...statuses.filter(status => !status.isVisible),
];

export const reorderStatusesByDisplayIndex = <T extends StatusWithVisibility>({
  statuses,
  sourceIndex,
  targetIndex,
}: {
  statuses: T[];
  sourceIndex: number;
  targetIndex: number;
}): T[] => {
  const displayOrder = getManageableStatusesDisplayOrder(statuses);
  const reordered = [...displayOrder];
  const [moved] = reordered.splice(sourceIndex, 1);
  reordered.splice(targetIndex, 0, moved);

  const reorderedIds = reordered.map(status => status._id);

  return [...statuses].sort((a, b) => reorderedIds.indexOf(a._id) - reorderedIds.indexOf(b._id));
};

export const getStatusFilterLabel = (status: FilterStatusItem) => status.label || status.name || status._id;

/** secondary.main is near-black in all modes; use for "add new" menu items and similar actions. */
export const accentActionSx = (theme: Theme) => ({
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.secondary.main,
  fontWeight: 500,
});

export const missingEmailPrefixStyle = {
  display: 'inline-block',
  marginLeft: '5px',
  fontSize: '11px',
  color: '#666',
  fontStyle: 'italic',
} as const;
